// Pure helpers for the page title and the notes row under the board (notesHeading names it).
// Spelling comes from theory.js (usesFlats / spellChordTone) so the row always
// matches the note names the fretboard draws.
import { MODES, DEGREE_LABELS, scaleNotes, noteIndex, ARPEGGIO_DICT, spellChordTone } from './theory';

/** Long names for arpeggio type keys, used in captions and screen-reader text. */
export const ARPEGGIO_TYPE_DISPLAY_NAME = {
  'Major': 'major',       'Minor': 'minor',
  'Sus2':  'sus2',        'Sus4':  'sus4',
  'Dim':   'diminished',  'Aug':   'augmented',
  'Maj6':  'major 6',     'Min6':  'minor 6',
  'Maj7':  'major 7',     'Min7':  'minor 7',
  'Dom7':  'dominant 7',
  'Maj9':  'major 9',     'Min9':  'minor 9',
  'Dom9':  'dominant 9',
  'Dim7':  'diminished 7',
  'm7b5':  'half-diminished 7',
  'Add9':  'add 9',       'mAdd9': 'minor add 9',
  '6/9':   'six-nine',    'm6/9':  'minor six-nine',
};

/**
 * @typedef {object} RowNote
 * @property {string} name   Spelled note name, e.g. 'Bb', 'F#'.
 * @property {string} degree Interval label as the fretboard shows it, e.g. '1', 'b3', '5'.
 * @property {boolean} isRoot
 */

/**
 * Heading for the notes row, named after what the row actually lists. Takes a Structure value
 * from any instrument (dobro: pass dobroStructureOf(category, type)).
 *   Scale, Double Stops -> "Notes in the scale" (double stops are drawn from the scale)
 *   Pentatonic -> "Notes in the pentatonic"; Modes -> "Notes in the mode"
 *   Arpeggios, Chords, Triad -> "Notes in the chord"
 *   Root & 5th -> "Root and fifth"; Chromatic -> "All twelve notes" (upright bass)
 * @param {string} structure
 * @returns {string}
 */
export function notesHeading(structure) {
  switch (structure) {
    case 'Pentatonic': return 'Notes in the pentatonic';
    case 'Modes':      return 'Notes in the mode';
    case 'Arpeggios':
    case 'Chords':
    case 'Triad':      return 'Notes in the chord';
    case 'Root & 5th': return 'Root and fifth';
    case 'Chromatic':  return 'All twelve notes';
    default:           return 'Notes in the scale';
  }
}

const MAJOR_SCALE = [0, 2, 4, 5, 7, 9, 11];
const MINOR_SCALE = [0, 2, 3, 5, 7, 8, 10];
const MAJOR_PENTA = [0, 2, 4, 7, 9];
const MINOR_PENTA = [0, 3, 5, 7, 10];

/** 'major' | 'Major' | undefined -> 'Major' | 'Minor' (anything not minor is major). */
function normalizeTonality(tonality) {
  return String(tonality || '').toLowerCase() === 'minor' ? 'Minor' : 'Major';
}

/**
 * Semitone formula for a scale-type structure ('Full Scale' | 'Pentatonic' | 'Modes').
 * @returns {number[]}
 */
function scaleFormula(structure, tonality, mode) {
  if (structure === 'Modes') return MODES[mode] ?? MODES.Ionian;
  const isMajor = normalizeTonality(tonality) === 'Major';
  if (structure === 'Pentatonic') return isMajor ? MAJOR_PENTA : MINOR_PENTA;
  return isMajor ? MAJOR_SCALE : MINOR_SCALE;
}

/**
 * Page title for the guitar: "G Major Scale", "G Minor Pentatonic", "G Dorian",
 * "G Maj7 Arpeggio".
 *
 * @param {object} p
 * @param {string} p.key
 * @param {string} p.tonality 'Major' | 'Minor'
 * @param {string} p.structure 'Full Scale' | 'Pentatonic' | 'Modes' | 'Arpeggios'
 * @param {string} [p.mode]
 * @param {string} [p.arpeggioType]
 * @returns {string}
 */
export function getGuitarTitle({ key, tonality, structure, mode, arpeggioType }) {
  if (structure === 'Modes') return `${key} ${mode || 'Ionian'}`;
  if (structure === 'Arpeggios') return `${key} ${arpeggioType || 'Major'} Arpeggio`;
  const noun = structure === 'Pentatonic' ? 'Pentatonic' : 'Scale';
  return `${key} ${normalizeTonality(tonality)} ${noun}`;
}

/**
 * Notes of the current scale, spelled the way the fretboard spells them, in scale order.
 * Degrees use the same chromatic labels as the fretboard's Intervals mode (DEGREE_LABELS),
 * so Dorian reads 1 2 b3 4 5 6 b7 and Lydian's raised fourth reads b5.
 *
 * @param {object} p
 * @param {string} p.key
 * @param {string} p.tonality 'Major' | 'Minor'
 * @param {string} p.structure 'Full Scale' | 'Pentatonic' | 'Modes'
 * @param {string} [p.mode]
 * @returns {RowNote[]}
 */
export function getNotesInKey({ key, tonality, structure, mode }) {
  const formula = scaleFormula(structure, tonality, mode);
  const names = scaleNotes(key, formula, normalizeTonality(tonality).toLowerCase());
  return formula.map((interval, i) => ({
    name: names[i],
    degree: DEGREE_LABELS[interval],
    isRoot: interval === 0,
  }));
}

/**
 * Notes of the current arpeggio (chord tones), spelled by degree so a b3 is always a
 * flatted third letter (G minor -> Bb). Same source as the fretboard's arpeggio caption.
 * Returns [] when the type does not exist for the tonality (a transient state while
 * Major/Minor is flipping).
 *
 * @param {object} p
 * @param {string} p.key
 * @param {string} p.tonality 'Major' | 'Minor'
 * @param {string} p.arpeggioType key of ARPEGGIO_DICT, e.g. 'Maj7'
 * @returns {RowNote[]}
 */
export function getNotesInChord({ key, tonality, arpeggioType }) {
  const entry = ARPEGGIO_DICT[`${arpeggioType}:${normalizeTonality(tonality)}`];
  if (!entry || noteIndex(key) === -1) return [];
  return entry.intervals.map((offset) => ({
    name: spellChordTone(key, offset, entry.labels[offset]),
    degree: entry.labels[offset],
    isRoot: offset === 0,
  }));
}
